import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { pickerMessages as m } from './pickerMessages';
import { useEditorSongs } from './useEditorSongs';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLPickerSongStepProps {
  songs: ReturnType<typeof useEditorSongs>;
  widgets: readonly SerializedWidget[];
  onPick: (song: CatalogItem) => void;
  onOpenRow: (widgetUUID: string) => void;
}

function statusOf(item: CatalogItem): 'upcoming' | 'out' | 'noDate' {
  if (item.releaseDate === null) {
    return Object.keys(item.serviceLinks ?? {}).length > 0 ? 'out' : 'noDate';
  }
  return Date.parse(item.releaseDate) > Date.now() ? 'upcoming' : 'out';
}

/** "Which song?" (design c6): the songs of the active artist; the song of the page first. */
const SMLPickerSongStepComponent: React.FC<SMLPickerSongStepProps> = ({ songs, widgets, onPick, onOpenRow }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const [query, setQuery] = useState('');
  const onQuery = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value), []);
  const blockOfSong = useMemo(() => {
    const map = new Map<string, string>();
    widgets.forEach((widget) => {
      if (widget.type === SmartLinkWidgetType.presave && typeof widget.data.catalogItemUuid === 'string') {
        map.set(widget.data.catalogItemUuid, widget.uuid);
      }
    });
    return map;
  }, [widgets]);
  const list = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const all =
      songs.pageSong === null ? songs.items : [songs.pageSong, ...songs.items.filter((one) => one !== songs.pageSong)];
    return needle === '' ? all : all.filter((item) => item.title.toLowerCase().includes(needle));
  }, [query, songs.items, songs.pageSong]);
  const addSong = useCallback(() => router.push(SMLRoutes.addSong), [router]);
  const retry = useCallback(() => {
    songs.refetch().catch(console.error);
  }, [songs]);

  if (songs.isLoading) {
    return <LoadingView />;
  }
  if (songs.error !== undefined) {
    return <ErrorView error={songs.error} retry={retry} />;
  }

  return (
    <div className={styles.songStep}>
      <div className={styles.search}>
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <input
          value={query}
          onChange={onQuery}
          placeholder={intl.formatMessage(m.songSearch)}
          aria-label={intl.formatMessage(m.songSearch)}
        />
      </div>
      {list.length === 0 && <p className={styles.empty}>{intl.formatMessage(m.songEmpty)}</p>}
      {list.map((item) => {
        const status = statusOf(item);
        const rowUUID = blockOfSong.get(item.uuid);
        const isPageSong = item === songs.pageSong;
        let statusLabel = m.songOut;
        if (rowUUID !== undefined) {
          statusLabel = m.songOnPage;
        } else if (status === 'upcoming') {
          statusLabel = m.songUpcoming;
        } else if (status === 'noDate') {
          statusLabel = m.songNoDate;
        }
        return (
          <button
            key={item.uuid}
            type="button"
            className={clsx(styles.songRow, isPageSong && styles.songRowOn, rowUUID !== undefined && styles.cardDim)}
            onClick={() => (rowUUID === undefined ? onPick(item) : onOpenRow(rowUUID))}
            data-testid={`picker-song-${item.uuid}`}
          >
            <span className={styles.songText}>
              <b>{item.title}</b>
              <small>
                {item.artistName}
                {isPageSong ? ` · ${intl.formatMessage(m.songOfPage)}` : ''}
              </small>
            </span>
            <span className={styles.songStatus}>{intl.formatMessage(statusLabel)}</span>
          </button>
        );
      })}
      <div className={styles.songFoot}>
        <span>{intl.formatMessage(m.songNotListed)}</span>
        <button type="button" className={styles.songAdd} onClick={addSong}>
          <FontAwesomeIcon icon={faPlus} />
          {intl.formatMessage(m.songAdd)}
        </button>
      </div>
    </div>
  );
};

export const SMLPickerSongStep = TypedMemo(SMLPickerSongStepComponent);
